import { UseFormReturnType } from "@mantine/form"
import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { isChartAlreadyAdded } from "@costory/shared/utils/digest"

import { PageType } from "@costory/front/pages/DigestV2/components/types"
import { getQueryFormatted } from "@costory/front/pages/DigestV2/helper"
import { useAddExplorerImageToNote } from "@costory/front/queries/digest"

interface UseChartActionsProps {
  form?: UseFormReturnType<Filters>
  setChartType?: (type: ExplorerChartType) => void
  setPage?: (page: PageType) => void
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  setLoading?: (loading: boolean) => void
}

export const useChartActions = ({
  form,
  setChartType,
  setPage,
  setEditContent,
  editContent,
  setLoading,
}: UseChartActionsProps) => {
  const addExplorerImageToNote = useAddExplorerImageToNote()

  const createChartUrl = useCallback(
    (
      filters: Filters,
      chartType: ExplorerChartType = ExplorerChartType.LINE,
    ) => {
      const chartFilters = {
        ...filters,
        explorerChartType: chartType,
      }
      return `${window.location.origin}/explorer?${getQueryFormatted(chartFilters)}`
    },
    [],
  )

  const isChartAdded = useCallback(
    (chartUrl: string) => {
      return isChartAlreadyAdded(editContent, chartUrl)
    },
    [editContent],
  )
  const [, setSearchParams] = useSearchParams()

  const handleQuickAdd = useCallback(
    async (
      e: React.MouseEvent,
      filters: Filters,
      chartType: ExplorerChartType = ExplorerChartType.LINE,
    ) => {
      e.stopPropagation()

      try {
        setLoading?.(true)
        setSearchParams({ mode: "preview" })

        await addExplorerImageToNote.mutateAsync({
          filters: { ...filters, explorerChartType: chartType },
          setEditContent,
        })
      } catch (error) {
        console.error("Failed to add chart to content:", error)
      } finally {
        setLoading?.(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addExplorerImageToNote, setEditContent, editContent, setLoading],
  )

  const handleAnalyze = useCallback(
    (
      filters: Filters,
      chartType: ExplorerChartType = ExplorerChartType.LINE,
    ) => {
      if (form && setChartType && setPage) {
        form.setValues(filters)
        setChartType(chartType)
        setPage(PageType.AdvancedExplorer)
      }
    },
    [form, setChartType, setPage],
  )

  return {
    addExplorerImageToNote,
    createChartUrl,
    isChartAdded,
    handleQuickAdd,
    handleAnalyze,
  }
}
